/**
 * Which board this page works on, and going to another: a space on a server, or back.
 *
 * A page served at `/s/<space>` works in that space, and leaves it for the server's app pages
 * (`/`: your teams and spaces). A GUI with its own core (the desktop app, the standalone build)
 * works on that core, its offline board, always there on this computer; opening a space of a
 * server reloads it with `?remote=<link>`, so every service talks to that space instead
 * (`app/services.ts` picks the transport), and "Back to this computer" reloads without it. The
 * own core is untouched meanwhile. Which spaces there are, and the right to enter them, come
 * from the accounts (`accounts.ts`).
 */
import type { SpaceLink } from "../client/space";
import { linkText } from "../client/space";
import type { CommandSet } from "../shared/commands";
import type { MenuSection } from "../shared/menus";

/** What the {@link Spaces} service offers. */
export interface Spaces {
  /** The space this page works in (`null`: a board that is not a space). */
  current: SpaceLink | null;
  /** Whether {@link Spaces.current} is a remote space, away from this GUI's own core. */
  remote: boolean;
  /** Whether this GUI has its own core on this computer (the desktop app, the standalone build). */
  onThisComputer: boolean;
  /** Whether a server served this page: its teams and spaces are then on the app pages (`/`). */
  served: boolean;
  /** Opens a space. */
  open(link: SpaceLink): void;
  /** Leaves the space: back to this computer's board, or to the server's app pages. */
  leave(): void;
}

/** The page's address, as {@link createSpaces} reads it. */
type Here = Pick<Location, "origin" | "pathname" | "search">;

/** What {@link createSpaces} needs. */
export interface SpacesDeps {
  /** The remote space, when the page was opened with one. */
  remote: SpaceLink | null;
  /** The page's own space (served at `/s/<space>`). */
  page: SpaceLink | null;
  /** Whether a server served the page and it talks to that server's cores. */
  served: boolean;
  /** The page's address now (tests give a fake one). */
  here?: () => Here;
  /** Loads another address (the browser's `location.assign`). */
  go?: (address: string) => void;
}

/** The page's parameters without `remote` (`?debug` and such stay), with `remote` set to `link` when given. */
export function addressWith(pathname: string, search: string, link: SpaceLink | null): string {
  const params = new URLSearchParams(search);
  if (link) params.set("remote", linkText(link));
  else params.delete("remote");
  const rest = params.toString();
  return `${pathname}${rest ? `?${rest}` : ""}`;
}

/** Starts the service. */
export function createSpaces(deps: SpacesDeps): Spaces {
  const here = deps.here ?? (() => location);
  const go = deps.go ?? ((address: string) => location.assign(address));
  return {
    current: deps.remote ?? deps.page,
    remote: !!deps.remote,
    onThisComputer: !deps.served,
    served: deps.served,
    open(link) {
      const { origin, pathname, search } = here();
      // A page of this server opens the server's own space page; anything else goes remote.
      if (deps.served && link.server === origin) go(addressWith(`/s/${link.space}`, search, null));
      else go(addressWith(pathname, search, link));
    },
    leave() {
      const { pathname, search } = here();
      if (deps.remote) go(addressWith(pathname, search, null));
      else if (deps.page) go(addressWith("/", search, null));
    },
  };
}

/** The spaces' shortcuts: leave the space. */
export function spacesCommands(spaces: Spaces): CommandSet {
  return { "space.leave": () => spaces.leave() };
}

/** The File menu's spaces: your teams and spaces, and leaving this one. */
export function spacesMenus(spaces: Spaces): MenuSection[] {
  return [
    {
      menu: "file",
      section: "Spaces",
      order: 20,
      entries: () => [
        { label: "Spaces…", command: "window.open", argument: "home" },
        ...(spaces.current ? [{ label: spaces.remote ? "Back to this computer" : "Leave this space", command: "space.leave" }] : []),
      ],
    },
  ];
}
